import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { DocSourceMissingError } from './doc-content';
import { queryKeys } from './query-client';
import type { HtmlDocumentKind, RenderedDocument } from '@wix/ai-scheduling-assistant-common-lib/documents';

// The client half of Office conversion: hands a signed document URL to `/api/convert-document` and gets back
// markdown, which `DocViewerBody` then renders through `MarkdownDocView`.
//
// `import type` only from the shared converters — that module pulls in an unzipper, and importing it for real
// here would drag it into the CLIENT bundle for the sake of two type names. The conversion runs on the pod
// precisely so it does not.

export type { HtmlDocumentKind, RenderedDocument };
// The old names, kept so call sites read the same while the payload moved from markdown to HTML.
export type ConvertibleKind = HtmlDocumentKind;
export type ConvertedDocument = RenderedDocument;

/** The route's structured refusals, kept apart from a transport failure. `unsupported` and `tooLarge` are
 *  PERMANENT — the viewer shows the no-retry hand-off for them, because trying again cannot help. */
export class DocumentUnsupportedError extends Error {
  constructor() {
    super('The document could not be read: unsupported');
    this.name = 'DocumentUnsupportedError';
  }
}

export class DocumentTooLargeError extends Error {
  constructor() {
    super('The document is over the conversion cap');
    this.name = 'DocumentTooLargeError';
  }
}

export async function fetchConvertedDocument(url: string, kind: HtmlDocumentKind): Promise<RenderedDocument> {
  const res = await apiFetch(`${API_BASE}/api/convert-document`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, kind }),
  });

  if (res.status === 422) throw new DocumentUnsupportedError();
  if (res.status === 413) throw new DocumentTooLargeError();
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  return (await res.json()) as RenderedDocument;
}

/** Extracted so the null-URL guard is testable without a renderer, exactly as `docTextQueryOptions` and
 *  `docBlobQueryOptions` are — `refetch()` in react-query v4 is not gated on `enabled`, so a Retry over a
 *  missing source would otherwise POST `url: null`. */
export function convertedDocumentQueryOptions(url: string | null, kind: HtmlDocumentKind | null) {
  const enabled = !!url && !!kind;
  return {
    queryKey: queryKeys.docConvert(url ?? '', kind ?? ''),
    queryFn: () => (url && kind ? fetchConvertedDocument(url, kind) : Promise.reject(new DocSourceMissingError())),
    enabled,
    ...CONVERSION_CACHE,
  };
}

/** How a conversion is cached, shared by the viewer and the thumbnails so the two cannot disagree about
 *  the same file.
 *
 *  **`staleTime: Infinity`** because the answer cannot change: the media URL is content-addressed, and a
 *  given URL + kind converts to the same HTML every time. Under the app's 30s default the viewer re-POSTed
 *  a document the cache was already holding — walk out of a folder, come back a minute later, and the pod
 *  converted it again for a byte-identical result.
 *
 *  **`cacheTime` is long but NOT infinite**, and that is the deliberate half. A converted deck is ~1.8MB of
 *  HTML, so a browsed folder of twenty is ~36MB held in memory; keeping every document a session ever
 *  opened would grow without bound on the one surface — a file browser — where that is most likely. Half an
 *  hour outlives any amount of navigating in and out of folders, which is the case this exists for, while
 *  still letting an abandoned document go. */
export const CONVERSION_CACHE_TIME_MS = 30 * 60 * 1000;

const CONVERSION_CACHE = { staleTime: Infinity, cacheTime: CONVERSION_CACHE_TIME_MS } as const;

/** The converted document for `url`, or an idle query when there is nothing to convert. `retry: false` is the
 *  app-wide default, so the viewer's explicit Retry is the only recovery. */
export function useConvertedDocument(url: string | null, kind: HtmlDocumentKind | null) {
  return useQuery(convertedDocumentQueryOptions(url, kind));
}
